import { DEFAULT_MEASURE_ITEMS, measureItemDefSchema } from "./measure-items";
import { rulesSettingSchema } from "./rules";
import { z } from "zod";
import { MEASUREMENT_PROMPT } from "./materials";
import { FRED_PROMPT } from "./fred";
import { ASSISTANT_SKILLS_DEFAULT, assistantSkillSchema } from "./assistant";
import { RECEIPT_PROMPT, paymentAccountsSchema } from "./receipts";
import { DEFAULT_RETAIL_PRICE_LIST, retailPriceListSchema } from "./retail";
import { DEFAULT_INSURANCE_TEMPLATE, DEFAULT_INSURANCE_TEXT, insuranceTemplateSchema } from "./compliance";
import { DEFAULT_OVERDUE_EMAIL, DEFAULT_OVERDUE_TEXT, emailTemplateSchema } from "./emailing";
import { DEFAULT_EMAIL_TEMPLATES, DEFAULT_TEXT_TEMPLATES, emailTemplatesSchema, textTemplatesSchema } from "./emailing";

/**
 * Settings registry. Each setting has a schema and a default.
 * Stored in `setting` as JSON; anything not stored uses the default.
 */

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex color like #D71920");
const shortList = z.array(z.string().trim().min(1).max(60)).max(50);
/** Alert times, minutes before (Google takes up to 5, up to 4 weeks before). */
export const alertList = z.array(z.number().int().min(0).max(40320)).max(5);
const hhmmSetting = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** One kind of record's linking to QuickBooks: how many in all, linked, ready to link, not found. */
const linkCounts = z.object({ total: z.number().int(), linked: z.number().int(), ready: z.number().int(), notFound: z.number().int(), notNeeded: z.number().int().optional() });
/**
 * A QuickBooks id ("35"). Stored settings are JSON, and the database layer turns an all-digit
 * string back into a number, so a number is taken too.
 */
/** A saved calendar view: which appointments, how shown, and the roles it opens to by default. */
export const calendarViewSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(60),
  view: z.enum(["month", "week", "day", "list", "crews"]),
  types: z.array(z.string().max(60)).max(30),
  userIds: z.array(z.string().uuid()).max(50),
  roleIds: z.array(z.string().uuid()).max(20),
});
export type CalendarView = z.infer<typeof calendarViewSchema>;

/** The Map page's filters (also what a saved map view keeps). */
export const mapFiltersSchema = z.object({
  /** Open jobs, closed (finished), lost, or all. */
  status: z.array(z.enum(["open", "closed", "lost"])).max(3).default(["open"]),
  workflowIds: z.array(z.string().uuid()).max(30).default([]),
  stageIds: z.array(z.string().uuid()).max(100).default([]),
  repIds: z.array(z.string()).max(50).default([]),
  /** Which date the range applies to. */
  dateField: z.enum(["created", "sold", "changed"]).default("created"),
  from: z.string().max(10).default(""),
  to: z.string().max(10).default(""),
  carrier: z.string().max(100).default(""),
  minValue: z.number().int().nonnegative().nullable().default(null),
  maxValue: z.number().int().nonnegative().nullable().default(null),
  search: z.string().max(100).default(""),
  colorBy: z.enum(["stage", "type", "rep"]).default("stage"),
  heat: z.boolean().default(false),
  hail: z.object({ on: z.boolean(), days: z.number().int().min(1).max(730), minSize: z.number().min(0.25).max(5), nearOnly: z.boolean(), miles: z.number().min(0.25).max(10) }).default({ on: false, days: 30, minSize: 1, nearOnly: false, miles: 1 }),
});
export type MapFilters = z.infer<typeof mapFiltersSchema>;
export const mapViewSchema = z.object({ id: z.string().min(1).max(40), name: z.string().trim().min(1).max(60), filters: mapFiltersSchema });
export type MapView = z.infer<typeof mapViewSchema>;

const qbId = z.union([z.string().max(40), z.number().int().nonnegative()]).transform(String);

export const settingsSchema = z.object({
  "company.name": z.string().trim().min(1).max(120),
  "company.timezone": z.string().min(1).max(64),
  "company.phone": z.string().trim().max(30),
  "company.email": z.string().trim().max(254),
  "company.address": z.string().trim().max(300),
  /** Who signs proposals for NMT ("Authorized Representative"); null: Ryan Etsell, with his signature on file. */
  "company.signerUserId": z.string().uuid().nullable(),
  /** The one person who can send addresses to the Rivian (Ryan's truck); nobody when unset. */
  "rivian.userId": z.string().uuid().nullable(),
  "company.license": z.string().trim().max(120),

  "security.inactivityTimeoutMinutes": z.number().int().min(5).max(43200),

  /** Alerts on appointments (minutes before; all-day ones count from midnight, so 360 is 6 PM the day before). */
  "calendar.alertsTimed": alertList,
  "calendar.alertsAllDay": alertList,
  /** Per appointment type: its own alerts, or "google" (each person's Google defaults; the CRM's reminders use the company's). */
  "calendar.alertsByType": z.record(z.string().max(60), z.union([alertList, z.literal("google")])),
  /** Task due reminders when a task has no reminder of its own: date-only tasks at this time that day; timed ones this long before (null: none). */
  "tasks.dueReminderAt": hhmmSetting.nullable(),
  "tasks.dueReminderMinutes": z.number().int().min(0).max(10080).nullable(),

  /** Red accents and buttons. */
  "branding.primaryColor": hexColor,
  /** Sidebar background. */
  "branding.sidebarColor": hexColor,
  "branding.logoKey": z.string().max(500).nullable(),

  "lists.leadSources": shortList,
  "lists.contactRoles": shortList,
  "lists.fileCategories": shortList,
  "lists.photoTags": shortList,
  "lists.appointmentTypes": shortList,
  "lists.expenseCategories": shortList,
  /** Cities that need a permit: their jobs show a reminder until one is added. */
  "lists.permitCities": shortList,

  /**
   * QuickBooks sub-customer name for new jobs. Placeholders: {number}, {lastName}, {type}.
   * Existing QuickBooks sub-customers are never renamed.
   */
  "jobs.qbNamePattern": z.string().trim().min(1).max(120),
  /** First job number when there are no jobs yet (JobNimbus numbers are kept on import). */
  "jobs.firstNumber": z.number().int().min(1).max(99_999_999),

  /** Workmanship warranty, in years: residential steep-slope roofing, and everything else. */
  "warranty.roofYears": z.number().int().min(0).max(50),
  /** Retail roof prices and costs per square, add-ons, and the margin warning. */
  "retail.priceList": retailPriceListSchema,
  /** AI photo tagging (off unless turned on): new photos get tags from the photo-tag list, and a caption. */
  "photos.aiTagging": z.boolean(),
  "photos.aiPrompt": z.string().trim().min(1).max(2000),
  /** What the receipt and measurement-report readers are told (the insurance estimate reader is the Worker's, unchanged). */
  "ai.receiptPrompt": z.string().trim().min(1).max(4000),
  "ai.measurementPrompt": z.string().trim().min(1).max(4000),
  /** What Fred, the assistant, is told; and how many messages each person can send him a day. */
  "ai.fredPrompt": z.string().trim().min(1).max(6000),
  "ai.fredDailyLimit": z.number().int().min(0).max(5000),
  /** Things Fred should know: the company's notes, added to his instructions on every message. */
  "ai.fredNotes": z.array(z.string().trim().min(1).max(500)).max(100),
  /** The personal assistant's skills: what Fred does with work that comes in on someone's own texts. */
  "ai.assistantSkills": z.array(assistantSkillSchema).max(30),
  /** "Email it": the subject, message and button for proposals, change orders and invoices. */
  "email.templates": emailTemplatesSchema,
  /** "Text it": the text for proposals, change orders and invoices (the link is added at the end). */
  "text.templates": textTemplatesSchema,
  /** Sub insurance: email subs before their workers' comp or liability expires (compliance.reminderDays ahead) and when it does. */
  /** A past-due invoice reminder: the email and the text (the link added as for "Email it"). */
  "email.overdueTemplate": emailTemplateSchema,
  "text.overdueTemplate": z.string().trim().min(1).max(600),
  "insurance.reminders": z.boolean(),
  "insurance.template": insuranceTemplateSchema,
  "insurance.textTemplate": z.string().trim().min(1).max(600),
  /** Saved calendar views (Sales, Production, custom), each the default for some roles. */
  "calendar.views": z.array(calendarViewSchema).max(30),
  /** Saved Map views (filters and overlays), for everyone. */
  "map.views": z.array(mapViewSchema).max(30),
  /** Email to Fred that can't be tied to a staff sender or a job's rep goes to this person (null: the first admin). */
  "fred.emailUserId": z.string().uuid().nullable(),
  /** Overdue tasks this many days past due go to the first admin (null: never). */
  "tasks.escalateAfterDays": z.number().int().min(1).max(60).nullable(),
  /** The company's Google review link: sent by hand from a job, and shown after a good survey score. */
  "reviews.googleUrl": z.string().trim().url().max(500).nullable(),
  /** Weather warnings on scheduled work: these appointment types, at or over these thresholds. */
  "weather.rainPercent": z.number().int().min(0).max(100),
  "weather.windMph": z.number().int().min(0).max(150),
  "weather.types": shortList,
  /** Material order catalog: what each item covers, and the defaults for new orders. */
  /** ABC Supply: NMT's ship-to account and the branch it orders from (Settings → Materials). */
  "abc.shipTo": z.object({ number: z.string().max(40), name: z.string().max(200), address: z.string().max(300) }).nullable(),
  "abc.branch": z.object({ number: z.string().max(20), name: z.string().max(200) }).nullable(),
  /** The ship-to's branches, as ABC listed them when it was picked. */
  "abc.branches": z.array(z.object({ number: z.string().max(20), name: z.string().max(200), home: z.boolean() })).max(100),
  /** Ask ABC for the catalog's prices every morning. */
  "abc.autoPrices": z.boolean(),
  /** Send material orders to ABC from the CRM (off: orders are placed with ABC by hand). */
  "abc.ordering": z.boolean(),
  "abc.pricesCheckedAt": z.string().max(40).nullable(),
  "materials.wastePercent": z.number().int().min(0).max(50),
  /** Extra roof measurement fields (vents, pipe flashings...), on every measurement. */
  "materials.measurementItems": z.array(measureItemDefSchema).max(40),
  /** Built-in rules: what the company changed (Settings → Automations → Built-in rules). */
  rules: rulesSettingSchema,
  "materials.supplier": z.string().trim().max(160),
  "warranty.otherYears": z.number().int().min(0).max(50),
  /** What the warranty covers, as printed on the certificate. */
  "warranty.terms": z
    .string()
    .trim()
    .min(1)
    .max(1200)
    .refine((t) => t.split("\n").length <= 20, "Keep the warranty wording to 20 lines or fewer so it fits on the certificate."),

  /** Card payments only up to this amount; above it, bank (ACH) only. */
  "payments.cardLimitCents": z.number().int().min(0).max(10_000_000),
  /** Credit-card surcharge passed to the customer, in basis points (300 = 3%, the most allowed). */
  "payments.surchargeBps": z.number().int().min(0).max(300),

  /** A sent work order with no answer from the sub after this many hours is flagged. */
  "workOrders.responseHours": z.number().int().min(1).max(336),
  /** Warn about subcontractor paperwork this many days before it expires. */
  "compliance.reminderDays": z.number().int().min(1).max(120),

  /**
   * Copy texts, calls, and voicemails on NMT's numbers from RingCentral. Off until turned on: the
   * RingCentral user also holds another business's number, whose messages are never kept.
   */
  "comms.syncEnabled": z.boolean(),
  /**
   * The RingCentral extensions the CRM watches (you, Jason, the call queue, the fax...): their texts,
   * calls, and voicemails are copied, and calls ringing them pop up for whoever is on them.
   */
  "comms.extensions": z.array(z.object({ id: z.string().regex(/^\d{1,20}$/), number: z.string().max(10), name: z.string().max(120) })).max(50),
  /**
   * NMT's phone numbers besides the main one (fax, direct lines), E.164. Only texts, calls, and
   * voicemails on NMT's numbers are kept: the same RingCentral account has other businesses' numbers.
   */
  "comms.numbers": z.array(z.string().regex(/^\+1\d{10}$/)).max(50),
  /**
   * Copy contacts (name and phone numbers) into each person's RingCentral address book, so the desk
   * phone and RingCentral app show who's calling. One way: the CRM is the source.
   */
  "comms.contactSync": z.boolean(),

  /** QuickBooks: the item on estimate and invoice lines, where payments go, what paid expenses come out of, and the expense account for each category. */
  "quickbooks.itemId": qbId.nullable(),
  /** Progress billing: retainage held is a negative line to this item, and its release bills it back. */
  "quickbooks.retainageItemId": qbId.nullable(),
  "quickbooks.depositAccountId": qbId.nullable(),
  "quickbooks.paidFromAccountId": qbId.nullable(),
  /** The accounts paid expenses can come out of (checking, each company card). */
  "quickbooks.paymentAccounts": paymentAccountsSchema,
  "quickbooks.expenseAccounts": z.record(z.string().max(60), qbId),
  /** The job P&L's "Job materials": these accounts (none chosen: any account named "...materials..."); every other expense is labor. */
  "quickbooks.materialAccountIds": z.array(qbId).max(20),
  /** The job P&L's commission: these accounts (none chosen: any account named "...admin subcontract..."), shown below net profit, not in it. */
  "quickbooks.commissionAccountIds": z.array(qbId).max(20),
  /** Where early-pay discounts taken go (a vendor credit to this account, on no job). */
  "quickbooks.discountAccountId": qbId.nullable(),
  /** QuickBooks refused a negative (discount) line once: discounts come off the items instead. */
  "quickbooks.foldDiscounts": z.boolean(),
  /** Take every early-pay discount offered (each bill can untick it). */
  "receipts.takeEarlyDiscounts": z.boolean(),
  /** The job P&L's overhead line, as a share of income. */
  "reports.overheadPercent": z.number().min(0).max(100),
  /** Where the last "changes since" read of QuickBooks got to (set by the server). */
  "quickbooks.changesSince": z.string().max(40).nullable(),
  /** How linking to QuickBooks stood at the last Sync (set by the server). */
  "quickbooks.linkCounts": z.object({ checkedAt: z.string().max(40), jobs: linkCounts, customers: linkCounts, vendors: linkCounts }).nullable(),
  /** When QuickBooks's change notices came and its changes were last read (set by the server). */
  "quickbooks.live": z.object({ lastNoticeAt: z.string().max(40).nullable(), lastChangesAt: z.string().max(40).nullable(), lastChanges: z.object({ expenses: z.number().int(), moneyIn: z.number().int() }).nullable(), lastError: z.string().max(500).nullable() }),
  /** JobNimbus isn't used anymore: the sync stops reading it; what came over stays. */
  "jobnimbus.retired": z.boolean(),

  /** Website lead form: off until a key is generated in Settings. */
  "leads.formKey": z.string().max(100).nullable(),
  "leads.workflowId": z.string().uuid().nullable(),
});

export type AppSettings = z.infer<typeof settingsSchema>;
export type SettingKey = keyof AppSettings;

export const SETTING_DEFAULTS: AppSettings = {
  "company.name": "NMT Construction",
  "company.timezone": "America/Chicago",
  "company.phone": "",
  "company.email": "",
  "company.address": "Moore, OK",
  "company.signerUserId": null,
  "rivian.userId": null,
  "company.license": "",

  "security.inactivityTimeoutMinutes": 60,

  "calendar.alertsTimed": [1440, 60],
  "calendar.alertsAllDay": [360],
  "calendar.alertsByType": {},
  "tasks.dueReminderAt": "09:00",
  "tasks.dueReminderMinutes": 60,

  "branding.primaryColor": "#D71920",
  "branding.sidebarColor": "#111111",
  "branding.logoKey": null,

  "lists.leadSources": ["Website", "Referral", "Phone call", "Door knock", "Yard sign", "Repeat customer", "Insurance agent", "Other"],
  "lists.contactRoles": ["Spouse", "Adjuster", "Carrier claims email", "Property manager", "Tenant", "Realtor", "Other"],
  "lists.fileCategories": ["Photos", "Contracts and proposals", "Insurance", "Measurements", "Permits", "Invoices and receipts", "Other"],
  "lists.photoTags": ["Before", "During", "After", "Damage", "Roof", "Gutters", "Siding", "Interior", "Materials", "Street View"],
  "lists.permitCities": [],
  "lists.expenseCategories": ["Materials", "Subcontractor labor", "Dump fees", "Permits", "Equipment rental", "Fuel", "Tools", "Meals", "Office", "Other"],
  "lists.appointmentTypes": ["Inspection", "Sales appointment", "Adjuster meeting", "Install", "Material delivery", "Follow-up", "Other"],

  "jobs.qbNamePattern": "{number} - {lastName} - {street} - {type}",
  "jobs.firstNumber": 1001,

  "warranty.roofYears": 5,
  "retail.priceList": DEFAULT_RETAIL_PRICE_LIST,
  "reviews.googleUrl": null,
  "tasks.escalateAfterDays": null,
  "map.views": [],
  "fred.emailUserId": null,
  "calendar.views": [
    { id: "sales", name: "Sales", view: "week", types: ["Inspection", "Sales appointment", "Adjuster meeting"], userIds: [], roleIds: [] },
    { id: "production", name: "Production", view: "crews", types: ["Install", "Material delivery"], userIds: [], roleIds: [] },
  ],
  "photos.aiTagging": false,
  "ai.receiptPrompt": RECEIPT_PROMPT,
  "ai.measurementPrompt": MEASUREMENT_PROMPT,
  "ai.fredPrompt": FRED_PROMPT,
  "ai.fredDailyLimit": 300,
  "ai.fredNotes": [],
  "ai.assistantSkills": ASSISTANT_SKILLS_DEFAULT,
  "email.templates": DEFAULT_EMAIL_TEMPLATES,
  "text.templates": DEFAULT_TEXT_TEMPLATES,
  "email.overdueTemplate": DEFAULT_OVERDUE_EMAIL,
  "text.overdueTemplate": DEFAULT_OVERDUE_TEXT,
  "insurance.reminders": true,
  "insurance.template": DEFAULT_INSURANCE_TEMPLATE,
  "insurance.textTemplate": DEFAULT_INSURANCE_TEXT,
  "photos.aiPrompt": "This is a photo from a roofing or construction job. Pick the tags that fit from the list (the stage of the work and what it shows), and write a short, plain caption (under 12 words) a homeowner or adjuster would understand. Don't guess at damage you can't see.",
  "weather.rainPercent": 50,
  "weather.windMph": 25,
  "weather.types": ["Install"],
  "abc.shipTo": null,
  "abc.branch": null,
  "abc.branches": [],
  "abc.autoPrices": false,
  "abc.ordering": false,
  "abc.pricesCheckedAt": null,
  "materials.wastePercent": 10,
  "materials.measurementItems": DEFAULT_MEASURE_ITEMS,
  rules: {},
  "materials.supplier": "ABC Supply",
  "warranty.otherYears": 1,
  "warranty.terms":
    "If a problem caused by our workmanship appears during this period, call us and we will inspect it and repair it at no charge. This warranty covers our labor and installation; it doesn't cover damage from storms, hail, wind beyond the materials' ratings, other trades, alterations, or lack of maintenance.",
  "payments.cardLimitCents": 200_000,
  "payments.surchargeBps": 300,
  "workOrders.responseHours": 24,
  "compliance.reminderDays": 30,

  "comms.syncEnabled": false,
  "comms.extensions": [],
  "comms.numbers": [],
  "comms.contactSync": false,

  "quickbooks.itemId": null,
  "quickbooks.retainageItemId": null,
  "quickbooks.depositAccountId": null,
  "quickbooks.paidFromAccountId": null,
  "quickbooks.paymentAccounts": [],
  "quickbooks.expenseAccounts": {},
  "quickbooks.materialAccountIds": [],
  "quickbooks.commissionAccountIds": [],
  "quickbooks.discountAccountId": null,
  "quickbooks.foldDiscounts": false,
  "receipts.takeEarlyDiscounts": true,
  "reports.overheadPercent": 10,
  "quickbooks.changesSince": null,
  "quickbooks.linkCounts": null,
  "quickbooks.live": { lastNoticeAt: null, lastChangesAt: null, lastChanges: null, lastError: null },
  "jobnimbus.retired": false,

  "leads.formKey": null,
  "leads.workflowId": null,
};

export const SETTING_KEYS = Object.keys(SETTING_DEFAULTS) as SettingKey[];

export function isSettingKey(key: string): key is SettingKey {
  return key in SETTING_DEFAULTS;
}

/** Merge stored values over defaults, dropping anything invalid or unknown. */
export function resolveSettings(stored: Record<string, unknown>): AppSettings {
  const out: Record<string, unknown> = { ...SETTING_DEFAULTS };
  for (const key of SETTING_KEYS) {
    if (!(key in stored)) continue;
    let parsed = settingsSchema.shape[key].safeParse(stored[key]);
    // The database layer turns an all-digit JSON string ("4052170668") back into a number.
    if (!parsed.success && typeof stored[key] === "number") parsed = settingsSchema.shape[key].safeParse(String(stored[key]));
    if (parsed.success) out[key] = parsed.data;
  }
  return out as AppSettings;
}

export const settingsUpdateSchema = settingsSchema.partial();

/**
 * QuickBooks sub-customer name for a job, from the pattern in settings.
 * QuickBooks display names are at most 100 characters and can't contain ":" (it separates parent and sub-customer).
 */
export function qbJobName(pattern: string, job: { number: number; lastName: string; type: string; street?: string }): string {
  const fill = (street: string) =>
    pattern
      .replaceAll("{number}", String(job.number))
      .replaceAll("{lastName}", job.lastName)
      .replaceAll("{street}", street)
      .replaceAll("{type}", job.type)
      .replaceAll(":", "-")
      .replace(/(\s*-\s*)+$|^(\s*-\s*)+/g, "") // a blank piece at either end
      .replace(/-(\s*-)+/g, "-") // or in the middle: "1042 - Smith - - Roof"
      .replace(/\s+/g, " ")
      .trim();
  const street = (job.street ?? "").trim();
  const full = fill(street);
  if (full.length <= 100 || !street || !pattern.includes("{street}")) return full.slice(0, 100);
  // Too long: shorten the street, so the number and last name stay whole.
  const room = street.length - (full.length - 100);
  return fill(room > 3 ? street.slice(0, room).trim() : "").slice(0, 100);
}
